<template>
  <section class="container-full-5">

    <h2 class="secondary-font gap-4 mt-2 mb-6">
      Mon revolving sur {{ souscription?.duree_mois || 12 }} mois
    </h2>

    <!-- Summary stats -->
    <div class="s20 flex items-center pb-2 gap-6">
      <div class="s7">
        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
          <div class="circle-medium bg-color-1000"></div>
          Décaissé
        </h5>
        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(totalDecaisse) }} F</h1>
        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissePourcent }}%</h5>
      </div>
      <div class="s7">
        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
          <div class="circle-medium bg-purple"></div>
          A décaisser
        </h5>
        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(totalADecaisser) }} F</h1>
        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ aDecaisserPourcent }}%</h5>
      </div>
      <div class="s6">
        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
          <div class="circle-medium bg-green-light"></div>
          Total
        </h5>
        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmount(totalGeneral) }} F</h1>
        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
      </div>
    </div>

    <!-- Progress bar empilée -->
    <div class="h-20 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
      <div class="h-100p flex align-items-center justify-content-left rounded-100 position-absolute text-xl bg-green-light" style="width: 100%;"></div>
      <div class="h-100p flex align-items-center justify-content-left rounded-100 position-absolute text-xl bg-purple text-white" :style="{ width: (parseFloat(aDecaisserPourcent) + parseFloat(decaissePourcent)) + '%' }"></div>
      <div class="h-100p flex align-items-center justify-content-left rounded-100 position-absolute text-xl bg-color-1000 text-white" :style="{ width: decaissePourcent + '%' }"></div>
    </div>

    <!-- Titre période -->
    <h2 class="secondary-font gap-4 mt-8 mb-6">
      Mois Revolving - {{ periodeLabel }}
    </h2>

    <!-- Cartes décaissement -->
    <div class="grid2 px-1">
      <div
        v-for="(detail, index) in details"
        :key="detail.id || index"
        class="widget border shadow-200 rounded-30 gap-5 px-7 py-5"
        :class="cardClass(detail, index)"
      >
        <div class="s10">
          <h6 class="secondary-font font-semibold mb-1">{{ formatDateFrench(detail.date_prevision) }}</h6>
          <h3 class="secondary-font font-semibold text-grey-900 py-1">{{ formatAmount(Math.abs(detail.utilisation_mois || 0)) }} F</h3>
          <p class="secondary-font font-semibold text-purple">{{ detailPourcent(detail) }}%</p>
        </div>
        <div class="s10 text-right">
          <!-- Countdown pour les mois à venir -->
          <h5
            v-if="detail.statut !== 'done' && countdownText(detail)"
            class="secondary-font font-semibold text-primary-color pb-1"
          >
            {{ countdownText(detail) }}
          </h5>
          <!-- Tag effectué -->
          <div class="mb-4" v-if="detail.statut === 'done'">
            <span class="tag-state notif stroke">Effectué</span>
          </div>
          <p class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
          <h6 class="secondary-font font-semibold text-grey-600 py-1">{{ formatDateShort(detail.date_operation || detail.date_prevision) }}</h6>
        </div>
      </div>
    </div>

    <div v-if="details.length === 0 && !loading" class="text-center text-grey-600 py-8">
      Aucun décaissement programmé
    </div>

    <div class="clear-both h-60"></div>

  </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { type SouscriptionDetail } from '@/services/profilService'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { formatAmount, formatDateFrench, formatDateShort } from '@/utils/formatters'

export default defineComponent({
  name: 'MonPouvoirAchat',
  setup() {
    const subscriptionStore = useSubscriptionStore()
    const loading = ref(true)

    const souscription = computed(() => subscriptionStore.souscription)
    const details = computed<SouscriptionDetail[]>(() => souscription.value?.details || [])

    const totalDecaisse = computed(() => {
      return details.value
        .filter(d => d.statut === 'done')
        .reduce((sum, d) => sum + Math.abs(d.decaissement || 0), 0)
    })

    const totalGeneral = computed(() => {
      return details.value.reduce((sum, d) => sum + Math.abs(d.decaissement || 0), 0)
    })

    const totalADecaisser = computed(() => totalGeneral.value - totalDecaisse.value)

    const decaissePourcent = computed(() => {
      if (totalGeneral.value === 0) return '0'
      return ((totalDecaisse.value / totalGeneral.value) * 100).toFixed(1)
    })

    const aDecaisserPourcent = computed(() => {
      if (totalGeneral.value === 0) return '0'
      return ((totalADecaisser.value / totalGeneral.value) * 100).toFixed(1)
    })

    const periodeLabel = computed(() => {
      if (!details.value.length) return '-'
      const mois = ['Jan.', 'Fév.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.']
      const first = new Date(details.value[0].date_prevision)
      const last = new Date(details.value[details.value.length - 1].date_prevision)
      return `${mois[first.getMonth()]} ${first.getFullYear()} - ${mois[last.getMonth()]} ${last.getFullYear()}`
    })

    function detailPourcent(detail: SouscriptionDetail): string {
      if (totalGeneral.value === 0) return '0'
      return ((Math.abs(detail.decaissement) / totalGeneral.value) * 100).toFixed(1)
    }

    function cardClass(detail: SouscriptionDetail, index: number): Record<string, boolean> {
      const firstPending = details.value.findIndex(d => d.statut !== 'done')
      return {
        'border-2 active': detail.statut === 'done' || index === firstPending,
        'border-3': detail.statut !== 'done' && index !== firstPending,
        'opacity-6': detail.statut !== 'done' && index > firstPending + 1
      }
    }

    function countdownText(detail: SouscriptionDetail): string {
      const target = new Date(detail.date_prevision)
      const now = new Date()
      const diff = target.getTime() - now.getTime()
      if (diff <= 0) return ''
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const months = Math.floor(days / 30)
      const remainDays = days % 30
      if (months > 0) return `${months}m ${remainDays}j`
      return `${days}j`
    }

    onMounted(async () => {
      loading.value = true
      try {
        await subscriptionStore.fetchSouscription()
      } finally {
        loading.value = false
      }
    })

    return {
      loading,
      souscription,
      details,
      totalDecaisse,
      totalADecaisser,
      totalGeneral,
      decaissePourcent,
      aDecaisserPourcent,
      periodeLabel,
      detailPourcent,
      cardClass,
      countdownText,
      formatAmount,
      formatDateFrench,
      formatDateShort
    }
  }
})
</script>
